import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, CanActivateChild, UrlTree } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { DatabaseCacheService } from '../../data/cache/database-cache.service';
import { StoreAuthService } from '../auth/store-auth.service';
import { AuthFlowService } from '../auth/auth-flow.service';

@Injectable()
export class ContractStatusGuard implements CanActivate, CanActivateChild {

  constructor(
    private router: Router,
    private storeAuth: StoreAuthService,
    private common: DatabaseCacheService,
    private authFlow: AuthFlowService,
    private msalService: MsalService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> {

    return this.checkPermission(route, state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> {

    return this.checkPermissionChild(route, state);
  }

  checkPermission(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> {

    // Allow access to unauthorized page regardless of contract status
    // Check the full URL since child routes aren't resolved yet in canActivate
    if (this.isUnauthorizedUrl(state.url)) {
      return of(true);
    }

    return from(this.authFlow.init()).pipe(
      switchMap(() => {
        if (!this.storeAuth.getIsLoggedIn() || !this.msalService.instance.getActiveAccount()) {
          // No active MSAL account — detect cross-tab logout or stale state
          if (this.storeAuth.getIsLoggedIn()) {
            this.storeAuth.setIsLoggedIn(false);
            this.storeAuth.setLoginUserName('');
            this.common.clearAuthCache();
          }
          return of(this.router.createUrlTree(['/']));
        }

        const cachedSectionWithRole = this.common.getSectionWithRole();
        if (cachedSectionWithRole && cachedSectionWithRole.length > 0) {
          // User is logged in with sections - check contract status for target section
          const sectionId = this.extractSectionId(next);
          return of(this.checkContractAndCreateUrlTree(sectionId, state.url));
        }

        const sectionId = this.extractSectionId(next);

        return from(this.common.setLoginUserAndSelectedSection(sectionId)).pipe(
          map(() => {
            const swr = this.common.getSectionWithRole();
            if (swr != null && swr.length > 0) {
              // Check contract status after login
              return this.checkContractAndCreateUrlTree(sectionId, state.url);
            }

            return this.router.createUrlTree(['/unauthorized']);
          }),
          catchError(() => of(this.router.createUrlTree(['/not-found'])))
        );
      })
    );
  }

  /**
   * Check contract status and return true (allow) or UrlTree (redirect to unauthorized)
   */
  private checkContractAndCreateUrlTree(sectionId: string | undefined, currentUrl?: string): boolean | UrlTree {
    const contractStatus = this.getContractStatusForSection(sectionId);

    if (contractStatus === 'CLOSE') {
      if (sectionId) {
        // Determine redirect path based on current route context
        const basePath = this.getRedirectBasePath(currentUrl);
        return this.router.createUrlTree(
          [basePath, sectionId, 'unauthorized'],
          { queryParams: { reason: 'contract' } }
        );
      }
      // No sectionId but contract is CLOSE - redirect to generic unauthorized
      return this.router.createUrlTree(['/unauthorized']);
    }

    return true;
  }

  /**
   * Determine the redirect base path based on current URL context
   */
  private getRedirectBasePath(url?: string): string {
    if (url?.includes('/section-admin')) {
      return '/section-admin';
    }
    return '/home';
  }

  private extractSectionId(route: ActivatedRouteSnapshot): string | undefined {
    let snapshot: ActivatedRouteSnapshot | null = route;
    while (snapshot) {
      const value = snapshot.paramMap.get('sectionId');
      if (value) {
        return value;
      }
      snapshot = snapshot.parent;
    }
    return undefined;
  }

  /**
   * Check if the URL is for the unauthorized page
   * Uses router.parseUrl for robust handling of query params, fragments, and matrix params
   */
  private isUnauthorizedUrl(url: string): boolean {
    const tree = this.router.parseUrl(url);
    const segments = tree.root.children['primary']?.segments.map(s => s.path) ?? [];
    return segments[segments.length - 1] === 'unauthorized';
  }

  /**
   * Check if the current route is the unauthorized page
   * Uses route config path matching for child guards
   */
  private isUnauthorizedRoute(route: ActivatedRouteSnapshot): boolean {
    // Check the route path directly
    let snapshot: ActivatedRouteSnapshot | null = route;
    while (snapshot) {
      if (snapshot.routeConfig?.path === 'unauthorized') {
        return true;
      }
      snapshot = snapshot.parent;
    }
    return false;
  }

  checkPermissionChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> {

    // Allow access to unauthorized page regardless of contract status
    if (this.isUnauthorizedRoute(next) || this.isUnauthorizedUrl(state.url)) {
      return of(true);
    }

    // If user is logged in and sectionDocuments is already loaded, check synchronously for better performance
    // Verify MSAL active account exists to detect cross-tab logout (stale in-memory state)
    if (this.storeAuth.getIsLoggedIn() && this.common.getSectionDocuments()) {
      if (!this.msalService.instance.getActiveAccount()) {
        // MSAL account gone (e.g. logged out in another tab) — clear stale state and redirect to root
        this.storeAuth.setIsLoggedIn(false);
        this.storeAuth.setLoginUserName('');
        this.common.clearAuthCache();
        return of(this.router.createUrlTree(['/']));
      }
      const urlSectionId = this.extractSectionId(next);
      return of(this.checkContractAndCreateUrlTree(urlSectionId, state.url));
    }

    // Data not loaded yet - wait for auth flow to complete
    return from(this.authFlow.init()).pipe(
      map(() => {
        const urlSectionId = this.extractSectionId(next);
        return this.checkContractAndCreateUrlTree(urlSectionId, state.url);
      }),
      catchError(() => of(this.router.createUrlTree(['/not-found'])))
    );
  }

  private getContractStatusForSection(sectionId: string | undefined): string | null {
    if (!sectionId) {
      return null;
    }

    // Get contract status from sectionDocuments (not from cached selected section)
    const sectionDocuments = this.common.getSectionDocuments();
    if (!sectionDocuments) {
      return null;
    }

    const section = sectionDocuments.find((doc: { id: string }) => doc.id === sectionId);
    const status = section?.contractInfo?.contractStatus;
    // Normalize to uppercase to handle potential case differences
    return status ? status.toUpperCase() : null;
  }
}
